<template>
  <div class="helpdesk-page">
    <section class="helpdesk-hero">
      <div class="hero-copy">
        <p class="hero-kicker">مرکز پشتیبانی</p>
        <h1>همراه شماییم</h1>
        <p class="hero-desc">
          درخواست‌ها مستقیم در هلپ‌دسک ثبت می‌شوند و تیم پشتیبانی پاسخ را همین‌جا می‌بیند.
        </p>
        <div class="hero-actions" v-if="canUse">
          <button
            v-if="!activeTicket"
            type="button"
            class="hero-cta"
            @click="openCreateModal"
          >
            <b-icon icon="plus-lg"></b-icon>
            تیکت جدید
          </button>
          <button
            v-if="unreadCount > 0 && !activeTicket"
            type="button"
            class="hero-ghost"
            @click="scrollToTickets"
          >
            {{ unreadCount }} پاسخ جدید
          </button>
        </div>
      </div>
      <div class="hero-visual" aria-hidden="true">
        <div class="hero-orb"></div>
        <div class="hero-badge">
          <b-icon icon="headset"></b-icon>
        </div>
      </div>
    </section>

    <div v-if="!routeEnabled || enabled === false" class="helpdesk-card helpdesk-off">
      پشتیبانی تیکت روی این داشبورد خاموش است.
      اگر این یک <strong>سایت مشتری</strong> است، در site-info فقط
      <code>sms_token</code> و <code>project_id</code> لازم است؛ مشخصات Odoo را اینجا نگذارید
      (Odoo فقط روی پنل با <code>ODOO_HELPDESK_ENABLED</code> روشن می‌شود).
      اگر پنل است، روی بک‌اند پنل <code>ODOO_HELPDESK_ENABLED=true</code> و آدرس/توکن Odoo را تنظیم کنید.
    </div>

    <div v-else-if="!ready" class="helpdesk-card helpdesk-off">
      برای فعال شدن تیکت این موارد را در تنظیمات سایت (site-info) تکمیل کنید:
      <ul class="mb-0 mt-2" v-if="missing && missing.length">
        <li v-for="(item, idx) in missing" :key="idx"><code>{{ item }}</code></li>
      </ul>
      <p class="mb-0 mt-2" v-else>شناسه پروژه و توکن پنل (sms_token) را بررسی کنید.</p>
    </div>

    <template v-else>
      <div class="helpdesk-grid" :class="{ 'is-thread': !!activeTicket }">
        <section ref="ticketsSection" class="helpdesk-card tickets-card">
          <template v-if="activeTicket">
            <helpdesk-ticket-thread
              :ticket="activeTicket"
              :messages="activeTicket.messages || []"
              :loading="detailLoading"
              :saving="saving"
              @close="closeTicket"
              @refresh="refreshTicket"
              @reply="onReply"
            />
          </template>
          <template v-else>
            <div class="list-head">
              <div>
                <p class="section-kicker">پیگیری</p>
                <h2>تیکت‌های من</h2>
              </div>
              <div class="list-actions">
                <button type="button" class="ghost-btn" :disabled="loading" @click="loadTickets">
                  بروزرسانی
                </button>
                <button type="button" class="primary-btn" @click="openCreateModal">
                  <b-icon icon="plus-lg"></b-icon>
                  تیکت جدید
                </button>
              </div>
            </div>

            <p v-if="loading" class="muted">در حال بارگذاری...</p>
            <div v-else-if="!tickets.length" class="empty-state">
              <div class="empty-icon">
                <b-icon icon="chat-dots"></b-icon>
              </div>
              <h3>هنوز تیکتی ثبت نشده</h3>
              <p>اولین درخواست پشتیبانی را ثبت کنید تا پاسخ تیم همین‌جا نمایش داده شود.</p>
              <button type="button" class="primary-btn" @click="openCreateModal">
                ثبت تیکت جدید
              </button>
            </div>
            <ul v-else class="ticket-list">
              <li
                v-for="ticket in tickets"
                :key="ticket.id"
                class="ticket-item"
                :class="{ 'is-unread': isUnread(ticket) }"
                @click="openTicket(ticket.id)"
              >
                <div class="ticket-main">
                  <strong>
                    <span v-if="isUnread(ticket)" class="unread-dot" aria-hidden="true"></span>
                    {{ ticket.name }}
                  </strong>
                  <span>{{ ticket.ticket_ref || ('#' + ticket.id) }}</span>
                </div>
                <div class="ticket-meta">
                  <em>{{ stageLabel(ticket) }}</em>
                  <small>{{ ticket.create_date || '' }}</small>
                  <span class="open-hint">{{ isUnread(ticket) ? 'پاسخ جدید' : 'مشاهده پاسخ‌ها' }}</span>
                </div>
              </li>
            </ul>
          </template>
        </section>

        <aside v-if="!activeTicket" class="helpdesk-aside">
          <section class="helpdesk-card contact-card">
            <p class="section-kicker">تماس با ما</p>
            <h2>نیاز به راهنمایی فوری دارید؟</h2>
            <p class="contact-desc">
              می‌توانید با پشتیبانی تماس بگیرید یا از طریق تیکت، درخواست خود را ثبت کنید.
            </p>
            <a class="phone-block" :href="'tel:' + supportPhone">
              <span class="phone-icon">
                <b-icon icon="telephone-fill"></b-icon>
              </span>
              <span>
                <small>شماره تماس پشتیبانی</small>
                <strong dir="ltr">{{ supportPhone }}</strong>
              </span>
            </a>
            <div class="contact-tips">
              <div>
                <b-icon icon="clock"></b-icon>
                <span>پاسخ تیکت‌ها در ساعات کاری بررسی می‌شود</span>
              </div>
              <div>
                <b-icon icon="shield-check"></b-icon>
                <span>درخواست‌ها مستقیم در سیستم پشتیبانی ثبت می‌شوند</span>
              </div>
            </div>
          </section>

          <section class="helpdesk-card tip-card">
            <h3>قبل از ثبت تیکت</h3>
            <ul>
              <li>موضوع را کوتاه و دقیق بنویسید</li>
              <li>مراحل بازتولید مشکل را ذکر کنید</li>
              <li>در صورت امکان تصویر یا کد خطا را اضافه کنید</li>
            </ul>
          </section>
        </aside>
      </div>
    </template>

    <b-modal
      id="helpdesk-create-modal"
      v-model="showCreateModal"
      hide-footer
      centered
      size="lg"
      modal-class="helpdesk-create-modal"
      title="ثبت تیکت جدید"
      @hidden="onModalHidden"
    >
      <p class="modal-lead">موضوع و شرح درخواست را وارد کنید تا برای تیم پشتیبانی ارسال شود.</p>
      <helpdesk-ticket-form
        v-if="showCreateModal"
        ref="ticketForm"
        id-prefix="page-modal"
        :saving="saving"
        @submit="createTicket"
      />
    </b-modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import HelpdeskTicketForm from '@/plugin/components/dashboard/Helpdesk/HelpdeskTicketForm.vue'
import HelpdeskTicketThread from '@/plugin/components/dashboard/Helpdesk/HelpdeskTicketThread.vue'
import {isOdooHelpdeskRouteEnabled} from '@/plugin/support/helpdeskRoute'

export default {
  name: 'HelpdeskTickets',
  components: {HelpdeskTicketForm, HelpdeskTicketThread},
  data() {
    return {
      showCreateModal: false,
      supportPhone: '09365249788',
    }
  },
  computed: {
    ...mapGetters({
      enabled: 'HelpdeskTicket/isEnabled',
      ready: 'HelpdeskTicket/isReady',
      missing: 'HelpdeskTicket/missing',
      tickets: 'HelpdeskTicket/tickets',
      activeTicket: 'HelpdeskTicket/activeTicket',
      loading: 'HelpdeskTicket/loading',
      detailLoading: 'HelpdeskTicket/detailLoading',
      saving: 'HelpdeskTicket/saving',
      isUnread: 'HelpdeskTicket/isUnread',
      unreadCount: 'HelpdeskTicket/unreadCount',
    }),
    routeEnabled() {
      return isOdooHelpdeskRouteEnabled()
    },
    canUse() {
      return this.routeEnabled && this.enabled !== false && this.ready
    },
  },
  created() {
    this.$store.dispatch('HelpdeskTicket/fetchStatus').then(() => {
      if (this.enabled !== false && this.ready) {
        this.loadTickets()
      }
    })
  },
  beforeDestroy() {
    this.$store.dispatch('HelpdeskTicket/clearActiveTicket')
  },
  methods: {
    loadTickets() {
      return this.$store.dispatch('HelpdeskTicket/fetchTickets', {force: true}).catch(() => {
      })
    },
    stageLabel(ticket) {
      return ticket.stage_name || 'ثبت شده'
    },
    openCreateModal() {
      this.showCreateModal = true
    },
    onModalHidden() {
      if (this.$refs.ticketForm) {
        this.$refs.ticketForm.reset()
      }
    },
    scrollToTickets() {
      const el = this.$refs.ticketsSection
      if (el && el.scrollIntoView) {
        el.scrollIntoView({behavior: 'smooth', block: 'start'})
      }
    },
    openTicket(ticketId) {
      this.$store.dispatch('HelpdeskTicket/fetchTicketDetail', ticketId).catch((error) => {
        const message = (error.response && error.response.data && error.response.data.message)
          || 'بارگذاری پاسخ‌های تیکت انجام نشد.'
        const text = typeof message === 'string' ? message : 'بارگذاری پاسخ‌های تیکت انجام نشد.'
        this.$bvToast.toast(text, {
          title: 'خطا',
          variant: 'danger',
          solid: true,
        })
      })
    },
    closeTicket() {
      this.$store.dispatch('HelpdeskTicket/clearActiveTicket')
    },
    refreshTicket() {
      if (!this.activeTicket || !this.activeTicket.id) {
        return
      }
      this.$store.dispatch('HelpdeskTicket/fetchTicketDetail', this.activeTicket.id).then((ticket) => {
        if (!ticket || !ticket.id) {
          this.$bvToast.toast('بروزرسانی تیکت انجام نشد.', {
            title: 'خطا',
            variant: 'danger',
            solid: true,
          })
          return
        }
        this.$bvToast.toast('پیام‌های تیکت بروزرسانی شد.', {
          title: 'پشتیبانی',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000,
        })
      }).catch((error) => {
        const message = (error.response && error.response.data && error.response.data.message)
          || 'بروزرسانی تیکت انجام نشد.'
        const text = typeof message === 'string' ? message : 'بروزرسانی تیکت انجام نشد.'
        this.$bvToast.toast(text, {
          title: 'خطا',
          variant: 'danger',
          solid: true,
        })
      })
    },
    onReply(payload, handlers = {}) {
      this.$store.dispatch('HelpdeskTicket/replyTicket', payload).then((data) => {
        if (handlers.onSuccess) {
          handlers.onSuccess(data || {})
        }
        this.$bvToast.toast('پیام شما ارسال شد.', {
          title: 'پشتیبانی',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000,
        })
        this.loadTickets()
      }).catch((error) => {
        const message = (error.response && error.response.data && error.response.data.message)
          || 'ارسال پیام انجام نشد.'
        const text = typeof message === 'string' ? message : 'ارسال پیام انجام نشد.'
        if (handlers.onError) {
          handlers.onError(text)
        }
        if (text.indexOf('کپتچا') !== -1 || text.indexOf('کپچا') !== -1) {
          if (handlers.onCaptchaRequired) {
            handlers.onCaptchaRequired()
          }
        }
        this.$bvToast.toast(text, {
          title: 'خطا',
          variant: 'danger',
          solid: true,
        })
      })
    },
    createTicket(form) {
      this.$store.dispatch('HelpdeskTicket/createTicket', form).then(() => {
        if (this.$refs.ticketForm) {
          this.$refs.ticketForm.reset()
        }
        this.showCreateModal = false
        this.$bvToast.toast('تیکت ثبت شد.', {
          title: 'پشتیبانی',
          variant: 'success',
          solid: true,
        })
      }).catch((error) => {
        const message = (error.response && error.response.data && error.response.data.message)
          || 'ثبت تیکت انجام نشد.'
        const text = typeof message === 'string' ? message : 'ثبت تیکت انجام نشد.'
        this.$bvToast.toast(text, {
          title: 'خطا',
          variant: 'danger',
          solid: true,
        })
      })
    },
  },
}
</script>

<style scoped>
.helpdesk-page {
  display: grid;
  gap: 18px;
  padding-bottom: 28px;
}

.helpdesk-hero {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1.4fr 0.8fr;
  gap: 20px;
  align-items: center;
  min-height: 220px;
  background:
    radial-gradient(420px 220px at 90% 10%, rgba(255, 255, 255, 0.18), transparent 60%),
    linear-gradient(125deg, #1e1e1e 0%, #3a1824 48%, #eb3d60 100%);
  color: #fff;
  border-radius: 28px;
  padding: 32px;
  box-shadow: 0 22px 44px rgba(235, 61, 96, 0.22);
}

.hero-kicker,
.section-kicker {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  opacity: 0.8;
}

.section-kicker {
  color: #eb3d60;
  opacity: 1;
}

.helpdesk-hero h1 {
  margin: 0 0 10px;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 800;
  line-height: 1.15;
}

.hero-desc {
  margin: 0;
  max-width: 34rem;
  opacity: 0.88;
  line-height: 1.8;
  font-size: 15px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
}

.hero-cta,
.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #1e1e1e;
  font-weight: 800;
  padding: 12px 20px;
  cursor: pointer;
}

.hero-ghost,
.ghost-btn {
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 999px;
  padding: 11px 18px;
  font-weight: 700;
  cursor: pointer;
}

.ghost-btn {
  border-color: #ead7dc;
  background: #fff;
  color: #1e1e1e;
}

.primary-btn {
  background: #eb3d60;
  color: #fff;
}

.hero-visual {
  position: relative;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-orb {
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.05) 55%, transparent 70%);
  filter: blur(2px);
}

.hero-badge {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  backdrop-filter: blur(8px);
}

.helpdesk-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.9fr);
  gap: 16px;
  align-items: start;
}

.helpdesk-grid.is-thread {
  grid-template-columns: 1fr;
}

.helpdesk-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(234, 215, 220, 0.9);
  border-radius: 24px;
  padding: 22px;
  box-shadow: 0 14px 32px rgba(30, 30, 30, 0.06);
}

.helpdesk-card h2,
.helpdesk-card h3 {
  margin: 0;
  font-weight: 800;
  color: #1e1e1e;
}

.helpdesk-card h2 {
  font-size: 22px;
}

.helpdesk-off {
  color: #1e1e1e;
  line-height: 1.8;
}

.list-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.list-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.muted {
  color: #6f5b62;
  margin: 0;
}

.empty-state {
  text-align: center;
  padding: 36px 16px 20px;
  display: grid;
  gap: 10px;
  justify-items: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #faf4f6;
  color: #eb3d60;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.empty-state h3 {
  margin: 8px 0 0;
  font-size: 18px;
}

.empty-state p {
  margin: 0 0 8px;
  color: #6f5b62;
  max-width: 28rem;
  line-height: 1.7;
}

.ticket-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.ticket-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 18px;
  background: #faf4f6;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ticket-item.is-unread {
  background: #ffe8ee;
  box-shadow: inset 3px 0 0 #eb3d60;
}

.ticket-item:hover {
  background: #f3e6ea;
  transform: translateY(-1px);
  box-shadow: 0 0 0 2px rgba(235, 61, 96, 0.12);
}

.ticket-item.is-unread:hover {
  background: #ffd9e3;
  box-shadow: inset 3px 0 0 #eb3d60, 0 0 0 2px rgba(235, 61, 96, 0.12);
}

.ticket-main strong {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1e1e1e;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #eb3d60;
  flex: 0 0 auto;
}

.ticket-item span,
.ticket-meta small {
  color: #6f5b62;
  font-size: 12px;
}

.ticket-meta {
  text-align: left;
  display: grid;
  gap: 4px;
  justify-items: end;
}

.ticket-meta em {
  font-style: normal;
  font-weight: 700;
  color: #eb3d60;
}

.open-hint {
  color: #eb3d60 !important;
  font-weight: 700;
}

.helpdesk-aside {
  display: grid;
  gap: 16px;
}

.contact-card h2 {
  margin-bottom: 8px;
  font-size: 20px;
}

.contact-desc {
  margin: 0 0 18px;
  color: #6f5b62;
  line-height: 1.8;
  font-size: 14px;
}

.phone-block {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(120deg, #1e1e1e, #3b1d2a);
  color: #fff !important;
  text-decoration: none !important;
  margin-bottom: 16px;
}

.phone-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: rgba(235, 61, 96, 0.9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex: 0 0 auto;
}

.phone-block small {
  display: block;
  opacity: 0.75;
  font-size: 12px;
  margin-bottom: 2px;
}

.phone-block strong {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.contact-tips {
  display: grid;
  gap: 10px;
}

.contact-tips div {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #6f5b62;
  font-size: 13px;
  line-height: 1.6;
}

.contact-tips .bi,
.contact-tips svg {
  color: #eb3d60;
  margin-top: 2px;
}

.tip-card h3 {
  margin-bottom: 12px;
  font-size: 16px;
}

.tip-card ul {
  margin: 0;
  padding: 0 18px 0 0;
  color: #6f5b62;
  line-height: 1.9;
  font-size: 14px;
}

.modal-lead {
  margin: 0 0 16px;
  color: #6f5b62;
  line-height: 1.7;
  font-size: 14px;
}

@media (max-width: 992px) {
  .helpdesk-hero,
  .helpdesk-grid {
    grid-template-columns: 1fr;
  }

  .hero-visual {
    min-height: 120px;
    order: -1;
  }

  .list-head {
    flex-direction: column;
  }

  .list-actions {
    width: 100%;
  }

  .list-actions .ghost-btn,
  .list-actions .primary-btn {
    flex: 1 1 auto;
    justify-content: center;
  }
}
</style>

<style>
.helpdesk-create-modal .modal-content {
  border: 0;
  border-radius: 22px;
  overflow: hidden;
}

.helpdesk-create-modal .modal-header {
  border-bottom: 1px solid #ead7dc;
  padding: 18px 22px;
}

.helpdesk-create-modal .modal-title {
  font-weight: 800;
  color: #1e1e1e;
}

.helpdesk-create-modal .modal-body {
  padding: 18px 22px 24px;
}
</style>
